import { ManageAttendanceFilters } from '../types/manage-attendance.types';

/** Case-insensitive "contains" clause for a Mongo filter. The gateway turns `$regex` into a literal
 * `contains`, so the text is never regex-escaped. Exported so the Department
 * lookups can build the same "search as you type" clause the keyword filter uses. */
export const containsFilter = (text: string) => ({
  $regex: text.trim(),
  $options: 'i',
});

/**
 * Builds the Mongo-style filter for the attendance/correction-request lists: optional status
 * equality, a case-insensitive keyword match on the employee name, and the department
 * clause. `departmentCondition` comes from `useDepartmentScope().getDepartmentIdCondition`, so a
 * reviewer limited to some departments is always restricted to them, whatever the picker says.
 * `extra` layers in scope the caller can't change through the UI (e.g. a single employee's
 * UserId on the drill-down history page).
 */
export const buildManageAttendanceFilter = (
  filters: ManageAttendanceFilters,
  departmentCondition: string | { $in: string[] } | undefined,
  extra: Record<string, unknown> = {}
): Record<string, unknown> => ({
  ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
  ...(filters.status ? { Status: filters.status } : {}),
  ...(filters.keyword.trim() ? { DisplayName: containsFilter(filters.keyword) } : {}),
  ...extra,
});

/**
 * The correction-request list's filter. Corrections store no name, so the keyword is matched
 * through the employees it finds: `keywordUserIds` are their IAM user ids, `undefined` while no
 * keyword is typed.
 */
export const buildCorrectionFilter = (
  filters: ManageAttendanceFilters,
  departmentCondition: string | { $in: string[] } | undefined,
  keywordUserIds?: string[]
): Record<string, unknown> =>
  buildManageAttendanceFilter(
    { ...filters, keyword: '' },
    departmentCondition,
    keywordUserIds ? { UserId: { $in: keywordUserIds } } : {}
  );
